import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X, Dumbbell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

export const ReminderBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [reminderData, setReminderData] = useState(null);
  const navigate = useNavigate();

  const checkReminder = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      // First check settings
      const settingsRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/settings`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!settingsRes.ok) return;
      
      const settings = await settingsRes.json();
      
      if (!settings.enabled || !settings.in_app_enabled) return;

      // Check if it's time to remind
      const now = new Date();
      const [hours, minutes] = settings.reminder_time.split(':').map(Number);
      const reminderTime = new Date();
      reminderTime.setHours(hours, minutes, 0, 0);

      // Only show if current time is within 1 hour after reminder time
      const timeDiff = now - reminderTime;
      if (timeDiff < 0 || timeDiff > 3600000) return; // 1 hour window

      // Check if already dismissed today
      const dismissedToday = localStorage.getItem('reminder_dismissed');
      if (dismissedToday === now.toDateString()) return;

      // Check if should remind
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/check-reminder`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.should_remind) {
          setReminderData(data);
          setShowBanner(true);
        }
      }
    } catch (error) {
      console.error('Error checking reminder:', error);
    }
  }, []);

  useEffect(() => {
    // Check on mount and every 5 minutes
    checkReminder();
    const interval = setInterval(checkReminder, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [checkReminder]);

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('reminder_dismissed', new Date().toDateString());
  };

  const handleGoToPractices = () => {
    setShowBanner(false);
    localStorage.setItem('reminder_dismissed', new Date().toDateString());
    navigate('/practices');
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed top-16 left-0 right-0 z-40 mx-4 sm:mx-auto sm:max-w-lg"
        >
          <div className="bg-gradient-to-r from-[#006039] to-[#005432] rounded-xl shadow-2xl overflow-hidden">
            <div className="p-4 flex items-center gap-4">
              <div className="flex-shrink-0 bg-white/20 p-3 rounded-full">
                <Bell className="h-6 w-6 text-white animate-pulse" />
              </div>
              
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold text-sm sm:text-base">
                  {reminderData?.message || 'Zeit für Deine tägliche Praxis!'}
                </p>
                {reminderData?.active_practices > 0 && (
                  <p className="text-white/80 text-xs sm:text-sm">
                    {reminderData.active_practices} aktive Praktik{reminderData.active_practices > 1 ? 'en' : ''} warten auf Dich
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={handleGoToPractices}
                  className="bg-white text-[#006039] hover:bg-white/90 font-semibold"
                >
                  <Dumbbell className="h-4 w-4 mr-1" />
                  Jetzt üben
                </Button>
                
                <button
                  onClick={handleDismiss}
                  className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-colors"
                  aria-label="Schließen"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ReminderBanner;
